import React, {
  useCallback, useEffect, useMemo, useRef 
} from 'react';
import classNames from 'classnames';
import isFunction from 'lodash/isFunction';

import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';

import { getUserDisplayName, getUserRole } from '../../../utils/name_utils';

import KebabVerticalIcon from '../../common/icons/KebabVerticalIcon';

import Avatar from '../../common/Avatar/Avatar';
import Badge from '../../common/Badge/Badge';
import BasicDropdown from '../../common/BasicDropdown/BasicDropdown';
import Label from '../../common/Labels/Label';
import VerticalGradientOverlay from '../../common/overlays/VerticalGradientOverlay';

import { UserType } from '../../../constants/user.constants';

const YouBadge = () => (
  <Badge className="p-2 text-sm font-bold" bgColor="bg-border-darker">You</Badge>
)

const UserItem = ({
  user,
  isDefaultWorkspaceForUser,
  onChangeRoleClick,
  onRemoveUserClick,
  isCurrentUser = false,
}) => {
  const hasRoleChangeFunction = onChangeRoleClick && isFunction(onChangeRoleClick);
  const handleChangeRole = useCallback((newRole) => {
    if (hasRoleChangeFunction) {
      onChangeRoleClick(user.id, newRole);
    }
  }, [hasRoleChangeFunction, onChangeRoleClick, user.id])

  const hasRemovalFunction = onRemoveUserClick && isFunction(onRemoveUserClick);
  const handleRemoveUser = useCallback(() => {
    if (hasRemovalFunction) {
      onRemoveUserClick(user.id);
    }
  }, [hasRemovalFunction, onRemoveUserClick, user.id])
  
  const handleValueSelected = useCallback((selectedId) => {
    if (selectedId === 'make-owner') {
      handleChangeRole(UserType.OWNER);
    } else if (selectedId === 'make-editor') {
      handleChangeRole(UserType.EDITOR);
    } else if (selectedId === 'make-viewer') {
      handleChangeRole(UserType.VIEWER);
    } else if (selectedId === 'remove-user') {
      handleRemoveUser(user.id);
    }
  }, [handleChangeRole, handleRemoveUser, user.id])

  const userOptions = useMemo(() => {
    const options = [];

    if (hasRoleChangeFunction) {
      // Normalize role: lowercase for comparison, map legacy "collaborator" → "editor"
      const rawRole = (user?.userType || user?.user_type)?.toLowerCase();
      const role = rawRole === 'collaborator' ? 'editor' : rawRole;
      
      // Show "Make X" for all roles except the current one
      if (role !== UserType.OWNER) {
        options.push({ id: 'make-owner', label: 'Make Owner' });
      }
      if (role !== UserType.EDITOR) {
        options.push({ id: 'make-editor', label: 'Make Editor' });
      }
      if (role !== UserType.VIEWER) {
        options.push({ id: 'make-viewer', label: 'Make Viewer' });
      }
    }

    if (hasRemovalFunction) {
      options.push({
        id: 'remove-user',
        label: 'Remove from Workspace',
      })
    }
    
    return options;
  }, [hasRemovalFunction, hasRoleChangeFunction, user?.userType, user?.user_type]);

  const selectionId = user?.email || user?.username;
  const isUserEnabled = user?.enabled;
  const isUserRemoved = !selectionId;

  return (
    <div id={`user-list-item-${selectionId}`} className="p-4 h-19 min-h-full w-full border rounded flex items-center justify-between">
      <div className="flex justify-start items-center">
        <Avatar />
        <div className="ml-2 flex flex-col">
          <Label>{getUserRole(user)}</Label>
          <Label>{getUserDisplayName(user)}</Label>
          {
            !isUserEnabled && !isUserRemoved && (
              <Label>(Inactive User)</Label>
            )
          }
        </div>
      </div>
      <div className="mt-1 flex justify-end items-center">
        <div className="mx-1 flex flex-wrap items-center">
          {
            isCurrentUser && (
              <YouBadge className="ml-1" />
            )
          }
        </div>
        {
          userOptions?.length && !isDefaultWorkspaceForUser ? (
            <BasicDropdown
              popoverClass="right-16 top-0"
              direction="right"
              dropdownLabel={null}
              iconFill="brand"
              iconSize={12}
              icon={KebabVerticalIcon}
              values={userOptions}
              onValueSelected={handleValueSelected}
              hideCaret
              hideBorder
            />
          ) : null
        }
      </div>
    </div>
  )
}

const ListRow = ({ index, style, data }) => {
  const {
    users,
    listRef,
    rowHeights,
    workspaceOwnerId,
    currentUserId,
    onChangeRoleClick,
    onRemoveUserClick,
    defaultWorkspaceUserId
  } = data;
  const rowRef = useRef({});
  const user = users?.length ? users[index] : null;
  const userID = user?.id;

  const setRowHeight = useCallback((rowIndex, size) => {
    listRef?.current?.resetAfterIndex(0);
    rowHeights.current = { ...rowHeights.current, [rowIndex]: size }
  }, [listRef, rowHeights]);

  useEffect(() => {
    if (rowRef.current) {
      setRowHeight(index, rowRef.current.clientHeight);
    }
  }, [index, setRowHeight]);

  const isOwner = workspaceOwnerId === userID;
  const isDefaultWorkspaceForUser = isOwner && defaultWorkspaceUserId;

  const hideControls = users?.length < 2 || isDefaultWorkspaceForUser || isDefaultWorkspaceForUser;

  return (
    <div style={style}>
      <UserItem
        key={userID}
        user={user}
        isDefaultWorkspaceForUser={isDefaultWorkspaceForUser}
        isOriginalOwner={userID === workspaceOwnerId}
        isCurrentUser={currentUserId === userID}
        onChangeRoleClick={!hideControls && onChangeRoleClick}
        onRemoveUserClick={!hideControls && onRemoveUserClick}
      />
    </div>
  )
}

const VirtualizedListSection = ({ 
  width,
  users,
  listRef,
  rowHeights,
  workspaceOwnerId,
  currentUserId,
  workspaceId,
  defaultWorkspaceId,
  currentUserRole,
  onChangeRoleClick,
  onRemoveUserClick,
  height,
  userItemSize,
  defaultWorkspaceUserId,
  getRowHeight
}) => {
  if (!users?.length) {
    return null;
  }

  return (
    <VariableSizeList
      ref={listRef}
      height={height}
      itemCount={users?.length || 0}
      estimatedItemSize={userItemSize}
      itemSize={getRowHeight}
      width={width}
      itemData={{
        users,
        listRef,
        rowHeights,
        workspaceOwnerId,
        currentUserId,
        workspaceId,
        defaultWorkspaceId,
        currentUserRole,
        onChangeRoleClick,
        onRemoveUserClick,
        defaultWorkspaceUserId
      }}
    >
      {ListRow}
    </VariableSizeList>
  )
}

export const UserList = ({
  className,
  currentUserId,
  workspaceId,
  workspaceOwnerId,
  defaultWorkspaceId,
  onChangeRoleClick,
  onRemoveUserClick,
  defaultWorkspaceUserId,
  users = [],
  userItemSize = 108,
  height = 222,
  disableScrollFading = false,
}) => {
  const listRef = useRef({});
  const rowHeights = useRef({});

  const currentUserWithRole = users?.find(({ id }) => id === currentUserId);
  const currentUserRole = currentUserWithRole?.userType;

  const getRowHeight = useCallback((index) => {
    const rowHeight = rowHeights?.current[index]
    if (rowHeight && rowHeight < userItemSize) {
      return userItemSize;
    }
    return rowHeight || userItemSize;
  }, [userItemSize, rowHeights]);

  return (
    <div className="relative z-0">
      <div className={classNames('overflow-scroll py-4 flex-grow', className)}>
        {
          !disableScrollFading && (
            <VerticalGradientOverlay className="top-0 left-0 right-0" fromDirection="b" />
          )
        }
        <AutoSizer disableHeight>
          {({ width }) => (
            <VirtualizedListSection
              width={width}
              users={users}
              listRef={listRef}
              rowHeights={rowHeights}
              workspaceOwnerId={workspaceOwnerId}
              currentUserId={currentUserId}
              workspaceId={workspaceId}
              defaultWorkspaceId={defaultWorkspaceId}
              currentUserRole={currentUserRole}
              onChangeRoleClick={onChangeRoleClick}
              onRemoveUserClick={onRemoveUserClick}
              height={height}
              userItemSize={userItemSize}
              getRowHeight={getRowHeight}
              defaultWorkspaceUserId={defaultWorkspaceUserId}
            />
          )}
        </AutoSizer>
        {
          !disableScrollFading && (
            <VerticalGradientOverlay className="bottom-0 left-0 right-0" fromDirection="t" />
          )
        }
      </div>
    </div>
  );
}

export default UserList;
